import React, { useState } from "react";
import { IconCancelCircle, IconSearch } from "@origin-digital/ods-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { Autocomplete as MuiAutocomplete, PaperProps } from "@mui/material";
import {
  AutocompleteChangeReason,
  AutocompleteCloseReason,
  AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";
import { PopperProps } from "@mui/material/Popper";
import { BASE_PATH } from "../../routes";
import { content } from "./content";
import { CardContent } from "../SearchResults/SearchResultItem";
import { NoSuggestions } from "./NoSuggestions";
import { SparkleIcon } from "../SearchResults/SparkleIcon";
import {
  StyledPopper,
  Container,
  StyledPaper,
  StyledTextField,
  StyledSearchIconContainer,
  StyledCancelIconButton,
  ExternalSearchActions,
  SearchSubmitButton,
  SparkleAdornment,
  HighlightMark,
  VisuallyHidden,
} from "./styles";
import { isLandingPath } from "../../helpers/routes";

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function highlightText(text: string, searchTerm: string): React.ReactNode {
  if (!searchTerm) return text;
  const regex = new RegExp(`(${escapeRegExp(searchTerm)})`, "ig");
  const segments = text.split(regex);
  return segments.map((segment, index) =>
    index % 2 === 1 ? (
      <HighlightMark key={`${segment}-${index}`}>{segment}</HighlightMark>
    ) : (
      <React.Fragment key={`${segment}-${index}`}>{segment}</React.Fragment>
    )
  );
}

export type SearchSuggestionOption = {
  value: string;
  title: string;
  url: string;
  summary?: string;
  tabCategory?: string;
  articleCategory?: string;
  articleCategoryUrl?: string | null;
};

type Props = {
  setInputValue: (value: string) => void;
  onFocusChange: (state: boolean) => void;
  inputValue: string;
  suggestions: SearchSuggestionOption[];
  // Optional with sensible defaults so existing test renders that don't care
  // about loading / no-results state can omit them. The Search wrapper
  // always passes both.
  isLoading?: boolean;
  resolvedQuery?: string;
  placeholder?: string;
  emptyState?: React.ReactNode;
  searchingContent?: React.ReactNode;
};

const PopperComponent = (props: PopperProps) => (
  <StyledPopper
    {...props}
    disablePortal
    modifiers={[
      ...(Array.isArray(props.modifiers)
        ? props.modifiers.filter((m: { name: string }) => m.name !== "offset")
        : []),
      { name: "offset", options: { offset: [0, -72] } },
    ]}
  />
);

type SearchInputFieldProps = {
  placeholder: string;
  inputValue: string;
  onFocusChange: (state: boolean) => void;
};

type SearchInputProps = SearchInputFieldProps & {
  autocompleteParams: AutocompleteRenderInputParams;
};

const SearchStartAdornment = ({
  appearance,
}: {
  appearance: "landing" | "default";
}): JSX.Element => (
  <StyledSearchIconContainer>
    <SparkleAdornment aria-hidden="true">
      <SparkleIcon appearance={appearance} />
    </SparkleAdornment>
  </StyledSearchIconContainer>
);

const createSearchInputAdornments = (appearance: "landing" | "default") => ({
  startAdornment: <SearchStartAdornment appearance={appearance} />,
});

type SearchInputRenderParams = Pick<
  AutocompleteRenderInputParams,
  "InputProps" | "inputProps" | "InputLabelProps"
>;

const createSearchInputMuiProps = (
  renderParams: SearchInputRenderParams,
  adornments: { startAdornment: JSX.Element; endAdornment?: JSX.Element }
) => ({
  ...renderParams,
  InputLabelProps: renderParams.InputLabelProps,
  InputProps: { ...renderParams.InputProps, ...adornments },
  inputProps: {
    ...renderParams.inputProps,
    "data-id": "search-input",
  },
});

const renderSearchInput = (
  restParams: Record<string, unknown>,
  muiProps: Record<string, unknown>,
  placeholder: string,
  onFocusChange: (focused: boolean) => void
) => (
  <StyledTextField
    {...restParams}
    muiProps={muiProps}
    onFocus={() => onFocusChange(true)}
    onBlur={() => onFocusChange(false)}
    hideHelperTextSpace
    id="autocomplete-input"
    placeholder={placeholder}
    variant="outlined"
    background="transparent"
  />
);

const SearchInput = ({
  placeholder,
  inputValue,
  onFocusChange,
  autocompleteParams,
}: SearchInputProps): JSX.Element => {
  const { InputLabelProps, InputProps, inputProps, ...restParams } =
    autocompleteParams;
  const { pathname } = useLocation();
  const sparkleAppearance = isLandingPath(pathname) ? "landing" : "default";
  const adornments = createSearchInputAdornments(sparkleAppearance);
  const muiProps = createSearchInputMuiProps(
    { InputProps, inputProps, InputLabelProps },
    adornments
  );
  return renderSearchInput(restParams, muiProps, placeholder, onFocusChange);
};

const createRenderInput =
  (fieldProps: SearchInputFieldProps) =>
  (autocompleteParams: AutocompleteRenderInputParams): JSX.Element =>
    <SearchInput {...fieldProps} autocompleteParams={autocompleteParams} />;

type SuggestionOptionProps = {
  inputValue: string;
};

function getOptionLabel(option: SearchSuggestionOption | string): string {
  return typeof option === "string" ? option : option.title;
}

const SuggestionOption =
  ({ inputValue }: SuggestionOptionProps) =>
  (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: SearchSuggestionOption | string
  ) => {
    if (typeof option === "string") return null;
    // Strip MUI's option-level onClick to avoid double dispatch with
    // our selectOption handling path.
    const { onClick: _muiOnClick, ...restProps } = props;
    return (
      <li
        {...restProps}
        onClick={() => {
          /* anchor inside CardContent handles navigation */
        }}
        data-id="search-suggestion"
        key={option.value}
      >
        <CardContent
          result={{
            rank: 0,
            title: option.title,
            url: option.url,
            summary: option.summary || "",
            allSummaryText: null,
            displayUrl: "",
            clickTrackingUrl: "",
            articleCategory: option.articleCategory,
            articleCategoryUrl: option.articleCategoryUrl ?? undefined,
          }}
          isRecommended={false}
          compact
          renderedTitle={highlightText(option.title, inputValue)}
        />
      </li>
    );
  };

export const Autocomplete = ({
  suggestions,
  inputValue,
  setInputValue,
  onFocusChange,
  isLoading = false,
  resolvedQuery = "",
  placeholder = "What are you looking for?",
  emptyState,
  searchingContent,
}: Props): JSX.Element => {
  return (
    <AutocompleteView
      suggestions={suggestions}
      inputValue={inputValue}
      setInputValue={setInputValue}
      onFocusChange={onFocusChange}
      isLoading={isLoading}
      resolvedQuery={resolvedQuery}
      placeholder={placeholder}
      emptyState={emptyState}
      searchingContent={searchingContent}
    />
  );
};

type AutocompleteViewProps = Omit<Props, "placeholder"> & {
  placeholder: string;
};

type AutocompleteControllerParams = {
  suggestions: SearchSuggestionOption[];
  inputValue: string;
  setInputValue: (value: string) => void;
  onFocusChange: (state: boolean) => void;
  isLoading: boolean;
  resolvedQuery: string;
  emptyState?: React.ReactNode;
  searchingContent?: React.ReactNode;
};

type AutocompleteControllerResult = {
  isOpen: boolean;
  hasNoResults: boolean;
  PaperComponent: (props: PaperProps) => JSX.Element;
  handleClose: (
    _e: React.SyntheticEvent,
    reason: AutocompleteCloseReason
  ) => void;
  handleFocusChange: (focused: boolean) => void;
  handleChange: (
    _e: React.SyntheticEvent,
    value: string | SearchSuggestionOption | null,
    reason: AutocompleteChangeReason
  ) => void;
  handleInputChange: (_e: React.SyntheticEvent, value: string) => void;
  submitSearch: (value: string) => void;
};

const blurActiveElement = (): void => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
};

const submitSearchValue = (
  navigate: ReturnType<typeof useNavigate>,
  setInputValue: (value: string) => void,
  searchValue: string
): void => {
  const normalizedValue = searchValue.trim();
  if (!normalizedValue) return;

  setInputValue(normalizedValue);
  navigate(`${BASE_PATH}/search?term=${encodeURIComponent(normalizedValue)}`);
  setTimeout(blurActiveElement, 0);
};

const handleSelectionChange = (
  submitSearch: (value: string) => void,
  value: string | SearchSuggestionOption | null,
  reason: AutocompleteChangeReason
): void => {
  if (reason === "createOption" && typeof value === "string") {
    submitSearch(value);
    return;
  }

  if (
    reason === "selectOption" &&
    value &&
    typeof value !== "string" &&
    value.url
  ) {
    window.location.href = value.url;
  }
};

const getOpenState = (
  suggestions: SearchSuggestionOption[],
  inputValue: string,
  isFocused: boolean,
  isLoading: boolean,
  resolvedQuery: string,
  emptyState?: React.ReactNode,
  searchingContent?: React.ReactNode
) => {
  const hasResults = suggestions.length > 0;
  const showEmptyContent = isFocused && !inputValue && !!emptyState;
  const showSearchingContent =
    isFocused && !!inputValue && !!searchingContent && !hasResults;

  // hasNoResults is true only after a request has SETTLED for the current
  // input with zero results — never during loading and never when the
  // resolved query is stale relative to the current input.
  const trimmedInput = inputValue.trim();
  const hasNoResults =
    !isLoading &&
    resolvedQuery.length > 0 &&
    resolvedQuery === trimmedInput &&
    suggestions.length === 0;

  // Controlled open: dropdown shows on focus when we have results, an
  // explicit empty-input state to surface, OR a settled no-results state
  // to communicate. Without focus the dropdown stays closed — including
  // on page reloads with ?term=foo, which would otherwise auto-open
  // without user engagement.
  const isOpen =
    isFocused &&
    (hasResults || hasNoResults || showEmptyContent || showSearchingContent);

  return {
    hasResults,
    showEmptyContent,
    showSearchingContent,
    hasNoResults,
    isOpen,
  };
};

const useAutocompleteDerivedState = (
  suggestions: SearchSuggestionOption[],
  inputValue: string,
  isFocused: boolean,
  isLoading: boolean,
  resolvedQuery: string,
  emptyState?: React.ReactNode,
  searchingContent?: React.ReactNode
) => {
  const {
    hasResults,
    showEmptyContent,
    showSearchingContent,
    hasNoResults,
    isOpen,
  } = getOpenState(
    suggestions,
    inputValue,
    isFocused,
    isLoading,
    resolvedQuery,
    emptyState,
    searchingContent
  );
  const PaperComponent = React.useMemo(
    () =>
      ({ children, ...props }: PaperProps) =>
        (
          <StyledPaper {...props}>
            {showSearchingContent ? (
              <div onMouseDown={(e) => e.preventDefault()}>
                {searchingContent}
              </div>
            ) : showEmptyContent ? (
              <div onMouseDown={(e) => e.preventDefault()}>{emptyState}</div>
            ) : (
              children
            )}
            {hasNoResults && <NoSuggestions query={resolvedQuery} />}
          </StyledPaper>
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `content` is a static module-level import; omitting it is intentional
    [
      hasResults,
      showSearchingContent,
      searchingContent,
      showEmptyContent,
      emptyState,
      hasNoResults,
      resolvedQuery,
    ]
  );

  return {
    isOpen,
    hasNoResults,
    PaperComponent,
  };
};

const useAutocompleteViewBindings = ({
  suggestions,
  inputValue,
  setInputValue,
  onFocusChange,
  isLoading = false,
  resolvedQuery = "",
  placeholder,
  emptyState,
  searchingContent,
}: AutocompleteViewProps) => {
  const controller = useAutocompleteController({
    suggestions,
    inputValue,
    setInputValue,
    onFocusChange,
    isLoading,
    resolvedQuery,
    emptyState,
    searchingContent,
  });

  const {
    hasNoResults,
    isOpen,
    PaperComponent,
    handleClose,
    handleFocusChange,
    handleChange,
    handleInputChange,
    submitSearch,
  } = controller;

  return {
    hasNoResults,
    submitSearch,
    autocompleteBindings: {
      open: isOpen,
      onClose: handleClose,
      options: suggestions,
      renderInput: createRenderInput({
        placeholder,
        inputValue,
        onFocusChange: handleFocusChange,
      }),
      PopperComponent,
      PaperComponent,
      renderOption: SuggestionOption({ inputValue }),
      getOptionLabel,
      inputValue,
      freeSolo: true,
      onChange: handleChange,
      onInputChange: handleInputChange,
    },
  };
};

const useAutocompleteController = (
  params: AutocompleteControllerParams
): AutocompleteControllerResult => {
  const {
    suggestions,
    inputValue,
    setInputValue,
    onFocusChange,
    isLoading,
    resolvedQuery,
    emptyState,
    searchingContent,
  } = params;
  const navigate = useNavigate();
  const [isFocused, setIsFocused] = useState(false);
  const submitSearch = (searchValue: string): void => {
    submitSearchValue(navigate, setInputValue, searchValue);
  };
  const { isOpen, hasNoResults, PaperComponent } = useAutocompleteDerivedState(
    suggestions,
    inputValue,
    isFocused,
    isLoading,
    resolvedQuery,
    emptyState,
    searchingContent
  );
  const handleClose = (
    _e: React.SyntheticEvent,
    reason: AutocompleteCloseReason
  ): void => {
    if (reason === "escape" || reason === "blur") {
      setIsFocused(false);
      onFocusChange(false);
    }
  };
  const handleFocusChange = (focused: boolean): void => {
    setIsFocused(focused);
    onFocusChange(focused);
  };
  const handleChange = (
    _e: React.SyntheticEvent,
    value: string | SearchSuggestionOption | null,
    reason: AutocompleteChangeReason
  ): void => {
    handleSelectionChange(submitSearch, value, reason);
  };
  const handleInputChange = (_e: React.SyntheticEvent, value: string): void => {
    setInputValue(value);
  };

  return {
    isOpen,
    hasNoResults,
    PaperComponent,
    handleClose,
    handleFocusChange,
    handleChange,
    handleInputChange,
    submitSearch,
  };
};

interface SearchActionButtonsProps {
  inputValue: string;
  setInputValue: (value: string) => void;
  submitSearch: (value: string) => void;
}

const SearchActionButtons = ({
  inputValue,
  setInputValue,
  submitSearch,
}: SearchActionButtonsProps): JSX.Element => (
  <>
    <StyledCancelIconButton
      data-id="clear-text-field"
      aria-label="Clear search text"
      aria-hidden={inputValue?.length === 0}
      disabled={inputValue?.length === 0}
      open={inputValue?.length > 0}
      onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
      }}
      onClick={() => setInputValue("")}
    >
      <IconCancelCircle color="neutralLight" />
    </StyledCancelIconButton>
    <SearchSubmitButton
      data-id="search-submit-button"
      aria-label="Submit search"
      onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
      }}
      onClick={() => submitSearch(inputValue)}
    >
      <IconSearch color="neutralDark" />
    </SearchSubmitButton>
  </>
);

const AutocompleteView = ({
  suggestions,
  inputValue,
  setInputValue,
  onFocusChange,
  resolvedQuery = "",
  isLoading = false,
  placeholder,
  emptyState,
  searchingContent,
}: AutocompleteViewProps): JSX.Element => {
  const { pathname } = useLocation();
  const showGlow = isLandingPath(pathname);
  const { autocompleteBindings, hasNoResults, submitSearch } =
    useAutocompleteViewBindings({
      suggestions,
      inputValue,
      setInputValue,
      onFocusChange,
      resolvedQuery,
      isLoading,
      placeholder,
      emptyState,
      searchingContent,
    });

  return (
    <Container showGlow={showGlow} isOpen={autocompleteBindings.open}>
      <MuiAutocomplete {...autocompleteBindings} />
      <ExternalSearchActions>
        <SearchActionButtons
          inputValue={inputValue}
          setInputValue={setInputValue}
          submitSearch={submitSearch}
        />
      </ExternalSearchActions>
      <VisuallyHidden role="status" aria-live="polite">
        {hasNoResults
          ? `${content.noResultsTemplate(resolvedQuery)} ${
              content.noResultsRetry
            }`
          : ""}
      </VisuallyHidden>
    </Container>
  );
};
